import {CreateIssueButton} from '@github-ui/issue-create/CreateIssueButton'
import {useNavigate} from '@github-ui/use-navigate'
import {CopyIcon, KebabHorizontalIcon, PencilIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {useCallback} from 'react'

import type {SavedView} from '../../components/views/types'
import {BUTTON_LABELS} from '../../constants/buttons'
import {LABELS} from '../../constants/labels'
import {useDeleteSavedViewDialog} from '../../hooks/use-delete-saved-view-dialog'
import {useDuplicateSavedViewDialog} from '../../hooks/use-duplicate-saved-view-dialog'
import styles from './SavedViewActions.module.css'

interface SavedViewActionsProps {
  savedView: SavedView
  owner: string
  repo: string
  viewsPath: string
  onStartEditing: () => void
  canManage: boolean
}

export function SavedViewActions({
  savedView,
  owner,
  repo,
  viewsPath,
  onStartEditing,
  canManage,
}: SavedViewActionsProps) {
  const navigate = useNavigate()
  const handleDeleted = useCallback(() => {
    navigate(viewsPath)
  }, [navigate, viewsPath])
  const {openDeleteDialog, deleteDialog} = useDeleteSavedViewDialog({owner, repo, onDeleted: handleDeleted})
  const {openDuplicateDialog, duplicateDialog} = useDuplicateSavedViewDialog({owner, repo})

  return (
    <>
      <div className={styles.actions}>
        <CreateIssueButton
          label={BUTTON_LABELS.newIssue}
          navigate={navigate}
          optionConfig={{
            issueCreateArguments: {
              repository: {owner, name: repo},
            },
            showRepositoryPicker: false,
            showFullScreenButton: true,
            canBypassTemplateSelection: true,
            navigate,
          }}
        />
        {canManage && (
          <ActionMenu>
            <ActionMenu.Anchor>
              <IconButton icon={KebabHorizontalIcon} aria-label={`${savedView.name} options`} variant="invisible" />
            </ActionMenu.Anchor>
            <ActionMenu.Overlay width="small">
              <ActionList>
                <ActionList.Item onSelect={onStartEditing}>
                  <ActionList.LeadingVisual>
                    <PencilIcon />
                  </ActionList.LeadingVisual>
                  {LABELS.views.edit}
                </ActionList.Item>
                <ActionList.Item onSelect={() => openDuplicateDialog(savedView)}>
                  <ActionList.LeadingVisual>
                    <CopyIcon />
                  </ActionList.LeadingVisual>
                  {LABELS.views.duplicate}
                </ActionList.Item>
                <ActionList.Item onSelect={() => openDeleteDialog(savedView)} variant="danger">
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  {LABELS.views.delete}
                </ActionList.Item>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        )}
      </div>
      {duplicateDialog}
      {deleteDialog}
    </>
  )
}
